import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Link as ChakraLink,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { FaTwitter, FaInstagram } from 'react-icons/fa';
import { createContext, useContext, useState, useCallback } from 'react';

const FadeContext = createContext();

function useFadeNavigate() {
  return useContext(FadeContext);
}

/* ─── Navbar ─── */

function Navbar() {
  const fadeNavigate = useFadeNavigate();
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      zIndex={10}
      bg="gray.900"
      py={0}
    >
      <Container maxW="6xl">
        <Flex justify="space-between" align="center" h={{ base: '56px', md: '76px' }}>
          <Image
            src="/attributed-to-logo-sm.webp"
            alt="attributed.to"
            h={{ base: '40px', md: '60px' }}
          />
          <HStack spacing={{ base: 3, md: 6 }} display={{ base: 'none', md: 'flex' }}>
            <ChakraLink
              href="#how-it-works"
              onClick={scrollTo('how-it-works')}
              color="whiteAlpha.700"
              fontSize="sm"
              fontWeight="400"
              _hover={{ color: 'white' }}
            >
              How It Works
            </ChakraLink>
            <ChakraLink
              href="#research-areas"
              onClick={scrollTo('research-areas')}
              color="whiteAlpha.700"
              fontSize="sm"
              fontWeight="400"
              _hover={{ color: 'white' }}
            >
              Research Areas
            </ChakraLink>
            <ChakraLink
              href="#for-collectors"
              onClick={scrollTo('for-collectors')}
              color="whiteAlpha.700"
              fontSize="sm"
              fontWeight="400"
              _hover={{ color: 'white' }}
            >
              For Collectors
            </ChakraLink>
            <ChakraLink
              href="#for-researchers"
              onClick={scrollTo('for-researchers')}
              color="whiteAlpha.700"
              fontSize="sm"
              fontWeight="400"
              _hover={{ color: 'white' }}
            >
              For Researchers
            </ChakraLink>
          </HStack>
          <HStack spacing={3}>
            <ChakraLink
              href="/login"
              onClick={fadeNavigate('/login')}
              color="whiteAlpha.700"
              fontSize="md"
              fontWeight="600"
              _hover={{ color: 'white' }}
            >
              Log In
            </ChakraLink>
            <Box h="14px" w="1px" bg="whiteAlpha.400" />
            <ChakraLink
              href="/signup"
              onClick={fadeNavigate('/signup')}
              color="whiteAlpha.700"
              fontSize="md"
              fontWeight="600"
              _hover={{ color: 'white' }}
            >
              Sign Up
            </ChakraLink>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

/* ─── Hero ─── */

function HeroSection() {
  const fadeNavigate = useFadeNavigate();
  return (
    <Box position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset={0}
        bgImage="url('/attributed-to-bg.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
      />
      <Box position="absolute" inset={0} bg="blackAlpha.700" />

      <Navbar />

      <Container maxW="6xl" position="relative" zIndex={1}>
        <Flex
          minH={{ base: '55vh', md: '60vh' }}
          align="center"
          justify="center"
          textAlign="center"
          direction="column"
          pt={{ base: 24, md: 32 }}
          pb={{ base: 12, md: 16 }}
        >
          <Text
            fontSize="sm"
            fontWeight="600"
            letterSpacing="0.15em"
            textTransform="uppercase"
            color="whiteAlpha.700"
            mb={5}
            textShadow="0 1px 3px rgba(0,0,0,0.4)"
          >
            The Art Research Marketplace
          </Text>
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
            fontWeight="300"
            color="white"
            lineHeight="1.15"
            maxW="4xl"
            letterSpacing="-0.02em"
            textShadow="0 2px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)"
          >
            Uncover the full story
            <br />
            behind every work of art
          </Heading>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color="whiteAlpha.800"
            maxW="2xl"
            mt={6}
            lineHeight="1.7"
            fontWeight="300"
            textShadow="0 1px 4px rgba(0,0,0,0.4)"
          >
            Connect with world-class art historians and researchers to improve
            your knowledge and increase the market value of your collection.
          </Text>
          <Button
            onClick={fadeNavigate('/signup')}
            size="lg"
            bg="orange.500"
            color="white"
            fontWeight="500"
            mt={10}
            px={10}
            py={7}
            fontSize="lg"
            _hover={{ bg: 'orange.600' }}
            rightIcon={<Icon as={FiArrowRight} />}
          >
            Get Started
          </Button>
        </Flex>
      </Container>
    </Box>
  );
}

/* ─── Example Bounty Card (static, non-clickable) ─── */

function ExampleBountyCard({ title, type, typeColor, reward, imageUrl, poster, time }) {
  return (
    <Box
      bg="white"
      rounded="xl"
      overflow="hidden"
      boxShadow="sm"
      borderWidth={1}
      borderColor="gray.100"
      _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
      transition="all 0.2s"
    >
      <Box h="180px" position="relative" overflow="hidden">
        <Image
          src={imageUrl}
          alt={title}
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="top"
        />
        <HStack position="absolute" top={3} left={3} right={3} justify="space-between" align="flex-start">
          <Badge
            bg={`${typeColor}.100`}
            color={`${typeColor}.800`}
            fontSize="xs"
            px={2}
            py={1}
            rounded="md"
          >
            {type}
          </Badge>
          <Box bg="white" rounded="lg" px={2} py={1} boxShadow="sm">
            <Text fontSize="sm" fontWeight="bold" color="green.600" lineHeight={1.3}>
              {reward}
            </Text>
          </Box>
        </HStack>
      </Box>
      <Box px={4} py={3}>
        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={2} mb={2} minH="2.5em">
          {title}
        </Text>
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" color="gray.400">Posted by</Text>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">{poster}</Text>
          </VStack>
          <Text fontSize="xs" color="gray.400">{time}</Text>
        </HStack>
      </Box>
    </Box>
  );
}

/* ─── Post Bounties Section ─── */

function PostBountiesSection() {
  const examples = [
    {
      title: 'Seeking attribution for 16th-century Flemish portrait',
      type: 'Attribution',
      typeColor: 'purple',
      reward: '$1,500',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F01-vermeyen-portrait.jpg?alt=media',
      poster: 'AntiquusAxis666',
      time: '2d ago',
    },
    {
      title: 'Identify coat of arms',
      type: 'Heraldry',
      typeColor: 'orange',
      reward: '$400',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F02-gelre-folio.jpg?alt=media',
      poster: 'GracilisNoctua121',
      time: '5d ago',
    },
    {
      title: 'Transcribe and translate Latin inscriptions in Book of Hours',
      type: 'Paleography',
      typeColor: 'pink',
      reward: '$200',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F03-punctus-interrogativus.jpg?alt=media',
      poster: 'InsulaForma46',
      time: '1w ago',
    },
  ];

  return (
    <Box id="how-it-works" bg="#faf9f7" py={{ base: 16, md: 24 }}>
      <Container maxW="6xl">
        <VStack spacing={4} mb={{ base: 12, md: 16 }} textAlign="center">
          <Text
            fontSize="sm"
            fontWeight="600"
            letterSpacing="0.15em"
            textTransform="uppercase"
            color="gray.500"
          >
            How It Works
          </Text>
          <Heading
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="300"
            color="gray.900"
            letterSpacing="-0.02em"
          >
            Post Tasks
          </Heading>
          <Text
            fontSize="md"
            color="gray.500"
            maxW="2xl"
            fontWeight="300"
            lineHeight="1.7"
          >
            Describe the research question about your artwork, set a reward,
            and verified researchers will submit evidence-backed findings.
            Review their work, and pay only when you're satisfied.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {examples.map((ex) => (
            <ExampleBountyCard key={ex.title} {...ex} />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

/* ─── Research Areas Section ─── */

function ResearchAreasSection() {
  const areas = [
    {
      title: 'Attribution',
      description: 'Determine which master artist or workshop created your artwork through stylistic and documentary analysis.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F04-old-master-copy.jpg?alt=media',
    },
    {
      title: 'Provenance',
      description: 'Trace the ownership history — original commission, prior collectors, exhibitions, and sales records.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F05-frederik-muller-auction.jpg?alt=media',
    },
    {
      title: 'Identification',
      description: 'Identify the subject, patron, symbolism, and iconographic program depicted in your work.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F06-durer-melancholia.jpg?alt=media',
    },
    {
      title: 'Technical Analysis',
      description: 'Dendrochronology, pigment analysis, canvas studies, and other scientific examination methods.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F07-irr-bonnefanten.jpg?alt=media',
    },
    {
      title: 'Heraldry',
      description: 'Decode coats of arms, monograms, collector marks, and other heraldic or symbolic elements.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F02-gelre-folio.jpg?alt=media',
    },
    {
      title: 'Paleography',
      description: 'Decipher and analyze historical handwriting, inscriptions, and manuscript annotations.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F08-advocatie-1404.jpg?alt=media',
    },
    {
      title: 'Archival Research',
      description: 'Uncover primary-source documentation in archives, inventories, catalogs, and historical records.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F09-national-archives.jpg?alt=media',
    },
    {
      title: 'Other',
      description: 'Customized solutions for unique research challenges, tailored to the needs of your collection.',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F10-restoration-frans-hals.jpg?alt=media',
    },
  ];

  return (
    <Box id="research-areas" bg="white" py={{ base: 16, md: 24 }}>
      <Container maxW="6xl">
        <VStack spacing={4} mb={{ base: 12, md: 16 }} textAlign="center">
          <Text
            fontSize="sm"
            fontWeight="600"
            letterSpacing="0.15em"
            textTransform="uppercase"
            color="gray.500"
          >
            Research Areas
          </Text>
          <Heading
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="300"
            color="gray.900"
            letterSpacing="-0.02em"
          >
            Expertise across every discipline
          </Heading>
          <Text
            fontSize="md"
            color="gray.500"
            maxW="2xl"
            fontWeight="300"
          >
            Our verified researchers cover the full spectrum of art-historical inquiry,
            from connoisseurship to scientific analysis.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
          {areas.map((area) => (
            <Box
              key={area.title}
              rounded="lg"
              overflow="hidden"
              borderWidth={1}
              borderColor="gray.100"
              transition="all 0.2s"
              _hover={{ borderColor: 'gray.300', shadow: 'md' }}
            >
              <Image
                src={area.imageUrl}
                alt={area.title}
                w="full"
                h="140px"
                objectFit="cover"
              />
              <Box p={5}>
                <Heading fontSize="md" fontWeight="500" color="gray.900" mb={2}>
                  {area.title}
                </Heading>
                <Text fontSize="sm" color="gray.500" lineHeight="1.65">
                  {area.description}
                </Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

/* ─── Citation Reference ─── */

const CITATIONS = [
  {
    id: 1,
    author: 'Radermecker, A.-S. V. E.',
    title: 'Artworks without names: an insight into the market for anonymous paintings',
    journal: 'Journal of Cultural Economics, Springer',
    year: 2019,
    url: 'https://scispace.com/pdf/artworks-without-names-an-insight-into-the-market-for-240aowi0do.pdf',
  },
  {
    id: 2,
    author: 'Etro, F. & Stepanova, E.',
    title: 'On the Efficiency of Art Markets: Evidence on Return Rates from Old Masters Paintings to Contemporary Art',
    journal: 'DISEI Working Paper N. 29/2019, Universit\u00e0 degli Studi di Firenze',
    year: 2019,
    url: 'https://www.disei.unifi.it/upload/sub/pubblicazioni/repec/pdf/wp29_2019.pdf',
  },
];

function CitationRef({ id }) {
  const cite = CITATIONS.find((c) => c.id === id);
  if (!cite) return null;
  return (
    <Popover trigger="hover" placement="top" openDelay={100} closeDelay={200}>
      <PopoverTrigger>
        <Text
          as="span"
          fontSize="xs"
          fontWeight="600"
          color="orange.500"
          cursor="pointer"
          _hover={{ color: 'orange.600' }}
          verticalAlign="super"
          ml="1px"
        >
          [{id}]
        </Text>
      </PopoverTrigger>
      <PopoverContent
        bg="gray.800"
        color="white"
        borderColor="gray.700"
        maxW="360px"
        _focus={{ outline: 'none' }}
      >
        <PopoverArrow bg="gray.800" />
        <PopoverBody fontSize="xs" lineHeight="1.6" p={3}>
          <Text fontWeight="600" mb={1}>{cite.author} ({cite.year})</Text>
          <Text fontStyle="italic" mb={2}>{cite.title}</Text>
          <Text color="gray.400" mb={1}>{cite.journal}</Text>
          <ChakraLink
            href={cite.url}
            isExternal
            color="orange.300"
            _hover={{ color: 'orange.200' }}
            fontSize="xs"
          >
            View paper &rarr;
          </ChakraLink>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Value Proposition (For Collectors) ─── */

function ValueProposition() {
  return (
    <Box id="for-collectors" bg="#faf9f7" py={{ base: 16, md: 24 }}>
      <Container maxW="6xl">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 20 }} alignItems="center">
          <Box>
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="gray.500"
              mb={4}
            >
              For Collectors
            </Text>
            <Heading
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="300"
              color="gray.900"
              lineHeight="1.3"
              letterSpacing="-0.02em"
              mb={6}
            >
              Research that protects
              and grows your investment
            </Heading>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.600" lineHeight="1.8" fontSize="md" fontWeight="300">
                Proper attribution is one of the most powerful levers in the art market.
                Research shows that paintings with a named artist connection sell for
                roughly <strong>3.9&times;</strong> the price of comparable works
                labeled as a generic "school."<CitationRef id={1} /> When an existing work is
                re-attributed from "school of" to a specific artist, auction returns from
                subsequent sales increase by approximately <strong>61%</strong>.<CitationRef id={2} />
              </Text>
              <Text color="gray.600" lineHeight="1.8" fontSize="md" fontWeight="300">
                Beyond financial value, deeper research brings the pleasure of truly understanding
                what you own — the story of who painted it, who commissioned it, and where it has
                been for centuries. Whether you collect for investment or for the love of art,
                scholarly research transforms your relationship with every piece.
              </Text>
            </VStack>
          </Box>

          <VStack spacing={0} align="stretch">
            <Box py={6} borderBottomWidth={1} borderColor="gray.200">
              <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="200" color="gray.900" lineHeight={1} mb={2}>
                3.9&times;
              </Text>
              <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                Price multiplier for works with a named artist versus an
                anonymous "school" label<CitationRef id={1} />
              </Text>
            </Box>
            <Box py={6}>
              <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="200" color="gray.900" lineHeight={1} mb={2}>
                +61%
              </Text>
              <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                Increase in auction returns when re-attributed from
                "school of" to a named artist<CitationRef id={2} />
              </Text>
            </Box>
          </VStack>
        </SimpleGrid>

        {/* Citation footnotes */}
        <Box mt={12} pt={6} borderTopWidth={1} borderColor="gray.200">
          <VStack align="stretch" spacing={1}>
            {CITATIONS.map((cite) => (
              <Text key={cite.id} fontSize="xs" color="gray.400" lineHeight="1.6">
                [{cite.id}] {cite.author} ({cite.year}). <em>{cite.title}</em>. {cite.journal}.{' '}
                <ChakraLink href={cite.url} isExternal color="gray.500" _hover={{ color: 'gray.700' }}>
                  PDF
                </ChakraLink>
              </Text>
            ))}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

/* ─── For Researchers Section ─── */

function ForResearchersSection() {
  const steps = [
    {
      number: '01',
      title: 'Create your profile',
      description: 'Apply for verification with your credentials and areas of expertise. Once approved, your profile is visible to collectors.',
    },
    {
      number: '02',
      title: 'Browse open tasks',
      description: 'Find research questions that match your specialization — attribution, provenance, heraldry, paleography, and more.',
    },
    {
      number: '03',
      title: 'Submit your findings',
      description: 'Upload evidence-backed research with citations and supporting documents. Collectors review your work before payment.',
    },
  ];

  return (
    <Box id="for-researchers" bg="white" py={{ base: 16, md: 24 }}>
      <Container maxW="6xl">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 16 }} alignItems="center">
          <Box>
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="gray.500"
              mb={4}
            >
              For Researchers
            </Text>
            <Heading
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="300"
              color="gray.900"
              lineHeight="1.3"
              letterSpacing="-0.02em"
              mb={6}
            >
              Earn from your expertise
            </Heading>
            <Text color="gray.600" lineHeight="1.8" fontSize="md" fontWeight="300" mb={8}>
              Art historians, conservators, and independent scholars can monetize their
              specialized knowledge by responding to collector research requests. Work on
              your own schedule, choose projects that match your expertise, and get paid
              for the research you love doing.
            </Text>
            <VStack align="stretch" spacing={6}>
              {steps.map((step) => (
                <HStack key={step.number} align="flex-start" spacing={4}>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="orange.400"
                    minW="28px"
                    pt="2px"
                  >
                    {step.number}
                  </Text>
                  <Box>
                    <Text fontWeight="500" color="gray.900" mb={1}>
                      {step.title}
                    </Text>
                    <Text fontSize="sm" color="gray.500" lineHeight="1.6">
                      {step.description}
                    </Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
          </Box>

          <SimpleGrid columns={2} spacing={4}>
            <Image
              src="https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F11-irr-predella.jpg?alt=media"
              alt="Infrared reflectography research"
              rounded="lg"
              objectFit="cover"
              h="200px"
              w="full"
            />
            <Image
              src="https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F12-bibliotheque-nationale.jpg?alt=media"
              alt="Historical manuscript research"
              rounded="lg"
              objectFit="cover"
              h="200px"
              w="full"
              mt={8}
            />
            <Image
              src="https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F13-venis-studios.jpg?alt=media"
              alt="Art conservation and restoration"
              rounded="lg"
              objectFit="cover"
              h="200px"
              w="full"
            />
            <Image
              src="https://firebasestorage.googleapis.com/v0/b/attributed.firebasestorage.app/o/landing%2F14-tableaux-maitres.jpg?alt=media"
              alt="Archival auction catalogue research"
              rounded="lg"
              objectFit="cover"
              h="200px"
              w="full"
              mt={8}
            />
          </SimpleGrid>
        </SimpleGrid>
      </Container>
    </Box>
  );
}

/* ─── CTA ─── */

function CTASection() {
  const fadeNavigate = useFadeNavigate();
  return (
    <Box bg="gray.900" py={{ base: 16, md: 24 }}>
      <Container maxW="3xl" textAlign="center">
        <Heading
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="300"
          color="white"
          letterSpacing="-0.02em"
          mb={4}
        >
          Start uncovering answers today
        </Heading>
        <Text
          color="whiteAlpha.700"
          fontSize="md"
          maxW="xl"
          mx="auto"
          mb={8}
          fontWeight="300"
          lineHeight="1.7"
        >
          Whether you're a collector seeking attribution or a scholar with expertise to share,
          attributed.to connects you with the right people.
        </Text>
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={4}
          justify="center"
        >
          <Button
            onClick={fadeNavigate('/signup')}
            size="lg"
            bg="orange.500"
            color="white"
            fontWeight="500"
            px={8}
            _hover={{ bg: 'orange.600' }}
            rightIcon={<Icon as={FiArrowRight} />}
          >
            Create Account
          </Button>
          <Button
            onClick={fadeNavigate('/login')}
            size="lg"
            variant="outline"
            color="orange.400"
            borderColor="orange.400"
            fontWeight="400"
            px={8}
            _hover={{ bg: 'whiteAlpha.100' }}
          >
            Sign In
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

/* ─── Footer ─── */

function Footer() {
  const fadeNavigate = useFadeNavigate();
  return (
    <Box bg="gray.900" borderTopWidth={1} borderColor="gray.800" py={8} pb={{ base: 72, xl: 8 }}>
      <Container maxW="6xl">
        <VStack align="flex-start" spacing={3}>
          <Text color="gray.500" fontSize="sm">
            &copy; {new Date().getFullYear()} attributed.to
          </Text>
          <HStack spacing={5} flexWrap="wrap">
            <ChakraLink
              href="/terms"
              onClick={fadeNavigate('/terms')}
              color="gray.600"
              fontSize="xs"
              _hover={{ color: 'gray.300' }}
            >
              Terms of Service
            </ChakraLink>
            <ChakraLink
              href="/privacy"
              onClick={fadeNavigate('/privacy')}
              color="gray.600"
              fontSize="xs"
              _hover={{ color: 'gray.300' }}
            >
              Privacy Policy
            </ChakraLink>
            <ChakraLink
              href="/login"
              onClick={fadeNavigate('/login')}
              color="gray.600"
              fontSize="xs"
              _hover={{ color: 'gray.300' }}
            >
              Sign In
            </ChakraLink>
            <ChakraLink
              href="/signup"
              onClick={fadeNavigate('/signup')}
              color="gray.600"
              fontSize="xs"
              _hover={{ color: 'gray.300' }}
            >
              Create Account
            </ChakraLink>
          </HStack>
          <HStack spacing={3} mt={2}>
            <ChakraLink href="https://x.com/Attributed_to" isExternal>
              <Icon as={FaTwitter} color="gray.600" boxSize={4} _hover={{ color: 'gray.300' }} />
            </ChakraLink>
            <ChakraLink href="https://www.instagram.com/attributed.to/" isExternal>
              <Icon as={FaInstagram} color="gray.600" boxSize={4} _hover={{ color: 'gray.300' }} />
            </ChakraLink>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
}

/* ─── Page ─── */

export default function Landing() {
  const [fading, setFading] = useState(false);
  const navigate = useNavigate();

  const fadeNavigate = useCallback((to) => (e) => {
    e.preventDefault();
    setFading(true);
    setTimeout(() => navigate(to), 200);
  }, [navigate]);

  return (
    <FadeContext.Provider value={fadeNavigate}>
      <Box opacity={fading ? 0 : 1} transition="opacity 0.2s ease">
        <HeroSection />
        <PostBountiesSection />
        <ResearchAreasSection />
        <ValueProposition />
        <ForResearchersSection />
        <Box position="relative" overflow="hidden">
          <CTASection />
          <Footer />
          <Image
            src="/footer-logo.webp"
            alt=""
            position="absolute"
            right={0}
            bottom="-10px"
            h={{ base: '200px', xl: '320px' }}
            pointerEvents="none"
          />
        </Box>
      </Box>
    </FadeContext.Provider>
  );
}
